import { isOutreachState, OUTREACH_STOPPED_STATES, type OutreachState } from '@discovr/types';

/**
 * TEMPORARY COMPATIBILITY SHIM: delete `translateLegacyStage` (and its test) once links shared before the status
 * model have aged out. Owner ruling: old `stage=outreach_*` links keep working, so each retired value is translated
 * on read to the states it now means (the row's roster status is `outreach` for all of them).
 *
 * `outreach_closed` is not one state any more. It becomes the multi-value filter over every stopped state, which the
 * wire already supports since `stage` is comma-joined; FilterPanel offers the same set as "Any stopped stage".
 */
const LEGACY_STAGE_STATES: Readonly<Record<string, readonly OutreachState[]>> = {
  outreach_sourced: ['sourced'],
  outreach_contacted: ['contacted'],
  outreach_replied: ['replied'],
  outreach_agreed: ['rate_provided'],
  outreach_closed: OUTREACH_STOPPED_STATES,
};

export function translateLegacyStage(token: string): readonly OutreachState[] | undefined {
  return Object.hasOwn(LEGACY_STAGE_STATES, token) ? LEGACY_STAGE_STATES[token] : undefined;
}

/** The stage filter value for "Any stopped stage": every stopped state, comma-joined like any multi-value stage. */
export const STOPPED_STAGE_VALUE = OUTREACH_STOPPED_STATES.join(',');

export interface ParsedStage {
  /** Comma-joined, validated states; absent when the param is empty or nothing in it was recognised. */
  stage?: string;
  /** Tokens that are neither a current state nor a legacy value. Never dropped silently: the tab tells the operator. */
  unrecognised: string[];
}

export function parseStageParam(raw: string | undefined): ParsedStage {
  const states = new Set<OutreachState>();
  const unrecognised: string[] = [];
  for (const token of (raw ?? '').split(',').map((part) => part.trim()).filter(Boolean)) {
    const translated = isOutreachState(token) ? [token] : translateLegacyStage(token);
    if (!translated) unrecognised.push(token);
    else translated.forEach((state) => states.add(state));
  }
  return { stage: states.size > 0 ? [...states].join(',') : undefined, unrecognised };
}
